import React from 'react'
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import { Button, IconButton } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import CustomTabs from "components/Shared/CustomTabs";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useLazyListCommunicationQuery } from "store/services/communicationApi";

import { useAppMeta, useTablePagination } from "hooks";

const CommunicationListScreen = () => {

    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const [searchParams] = useSearchParams();

    const activeTab = searchParams.get("tab") || "Active";
    const keyword = searchParams.get("search") || "";

    const breadcrumbs = [{ title: "Communication" }];

    const communicationTabs = [
        { label: "Active", value: "Active" },
        { label: "Inactive", value: "Inactive" },
    ];

    const columns = [
        {
            name: "Communication ID",
            selector: (row) => row?.communicationId,
            cell: (row) => row?.communicationId
        },
        {
            name: "Shipment ID",
            selector: (row) => row?.shipment?.shipmentId,
            cell: (row) => row?.shipment?.shipmentId,
        },
        {
            name: "Sender System",
            selector: (row) => row?.senderSystem,
            cell: (row) => row?.senderSystem,
        },
        {
            name: "Receiver System",
            selector: (row) => row?.receiverSystem,
            cell: (row) => row?.receiverSystem,
        },
        {
            name: "Communication Type",
            selector: (row) => row?.communicationType,
            cell: (row) => row?.communicationType,
        },
        {
            name: "Message",
            selector: (row) => row?.message,
            cell: (row) => row?.message,
        },
        {
            name: "Loading Time",
            selector: (row) => <AppTableDateTime date={row?.communicationTime} />,
        },

        {
            name: "Status",
            selector: (row) => row?.status,
            cell: (row) => row?.status
        },

        {
            name: "Actions",
            cell: (row) => (
                <>
                    <Link
                        to={`/app/crm/communication/${row?._id}/detail`}>
                        <IconButton size="small" aria-label="View Communication" color="primary">
                            <InfoOutlined />
                        </IconButton>
                    </Link>
                    <Link
                        to={`/app/crm/communication/${row?._id}/edit`}>
                        <IconButton size="small" aria-label="Edit Communication">
                            <ModeEditOutlineTwoTone />
                        </IconButton>
                    </Link>
                </>
            ),
            ignoreRowClick: true,
            right: true,
        },

    ];

    // RTK Query
    const [fetchCommunication, { data: communicationRes, isLoading, isFetching, error }] =
        useLazyListCommunicationQuery();

    React.useEffect(() => {
        fetchCommunication({
            active: activeTab === "Active",
            page: currentPage,
            limit: perPage,
            ...(keyword && { keyword })
        });
    }, [activeTab, currentPage, fetchCommunication, keyword, perPage])

    useAppMeta(`${activeTab} Communication`);
    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
            <BreadcrumbLayout.Error error={error} />

            <BreadcrumbLayout.Paper>
                <CustomTabs tabs={communicationTabs} activeTab={activeTab} />

                <TableFilter
                    searchLabel="Communication ID"
                    disabled={isLoading || isFetching}
                >
                    <div className="font-medium"> {activeTab} Communication </div>
                </TableFilter>

                <AppTable
                    columns={columns}
                    data={communicationRes?.data ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={communicationRes?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    )
}

export default CommunicationListScreen;

const PageActions = () => {
    return (
        <>
            <Link to="/app/crm/communication/create">
                <Button startIcon={<Add />} variant="contained" color="primary">
                    Add Communication
                </Button>
            </Link>
        </>
    );
}